import React from "react";
import { Facebook, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV_LINKS, LEGAL_LINKS, SOCIALS, CONTACT } from "../data";
import { useSectionNav } from "../hooks/useSectionNav";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const socialIcon = {
  facebook: Facebook,
  linkedin: Linkedin,
  x: XIcon,
};

const NAVIGARE = NAV_LINKS.slice(0, 4);
const COMPANIE = [...NAV_LINKS.slice(4), { label: "Contact", href: "#contact" }];

export const Footer = () => {
  const goSection = useSectionNav();

  const NavLinkItem = ({ l }) => (
    <li>
      <a
        href={`/${l.href}`}
        onClick={(e) => {
          e.preventDefault();
          goSection(l.href.replace("#", ""));
        }}
        data-testid={`footer-nav-${l.href.replace("#", "")}`}
        className="text-sm text-ink-muted hover:text-brand transition-colors"
      >
        {l.label}
      </a>
    </li>
  );

  return (
    <footer className="bg-white border-t border-gray-100" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-6">
          <div className="col-span-2 lg:col-span-2">
            <Logo />
            <p className="mt-5 text-sm text-ink-muted leading-relaxed max-w-xs">
              Servicii complete de contabilitate, fiscalitate și consultanță pentru
              companii din întreaga Românie.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => {
                const Icon = socialIcon[s.type];
                return (
                  <a
                    key={s.type}
                    href={s.href}
                    aria-label={s.label}
                    data-testid={`social-${s.type}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 text-ink-muted hover:border-brand hover:text-brand hover:-translate-y-0.5 transition-[color,border-color,transform] duration-300"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-ink uppercase tracking-wide">Navigare</h4>
            <ul className="mt-5 space-y-3">
              {NAVIGARE.map((l) => (
                <NavLinkItem key={l.href} l={l} />
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-ink uppercase tracking-wide">Companie</h4>
            <ul className="mt-5 space-y-3">
              {COMPANIE.map((l) => (
                <NavLinkItem key={l.href} l={l} />
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-ink uppercase tracking-wide">Legal</h4>
            <ul className="mt-5 space-y-3">
              {LEGAL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-legal-${l.to.replace("/", "")}`}
                    className="text-sm text-ink-muted hover:text-brand transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-ink uppercase tracking-wide">Contact</h4>
            <ul className="mt-5 space-y-3 text-sm text-ink-muted">
              <li>{CONTACT.company}</li>
              <li>{CONTACT.address}</li>
              <li>
                <a href={CONTACT.phoneHref} className="hover:text-brand transition-colors">
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} className="hover:text-brand transition-colors">
                  {CONTACT.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-ink-muted">
            © {new Date().getFullYear()} AN CONTAB AUDIT. Toate drepturile rezervate.
          </p>
          <p className="text-sm text-ink-muted">
            AN CONTAB AUDIT — <span className="text-brand font-medium">Lighting in Accounting</span>
          </p>
        </div>
      </div>
    </footer>
  );
};
